import isEmpty from 'lodash/isEmpty';
import {
  select,
  call,
  put,
  take,
  takeEvery,
  actionChannel,
} from 'redux-saga/effects';

import API from '../../api';
import { BooksActionTypes } from '../actions/types';
import {
  BooksActionCreators,
  ModalsActionCreators,
} from '../actions/creators';
import { AsyncActionStatus } from '../enums';
import { generateErrorMessage } from '../helpers';
import Mixpanel from '../../../helpers/mixpanel';
import { BooksSelectors } from '../selectors';
import { toast } from '../../utilities';

function* submitBookDraftToEditorByWriter(action) {
  try {
    const response = yield call(
      API.Books.submitBookDraftToEditorByWriter,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.submitBookDraftToEditorByWriter[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    Mixpanel.track('Writer draft submission');
    yield put(ModalsActionCreators.openDraftApprovedByWriterModal());
    yield call(toast.success, t('toastMessages.success.draftSubmitted', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.submitBookDraftToEditorByWriter[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* submitBookDraftToEditorByDevelopmentalEditor(action) {
  try {
    yield call(
      API.Books.submitBookDraftToEditorByDevelopmentalEditor,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.submitBookDraftToEditorByDevelopmentalEditor[AsyncActionStatus.FULFILLED]());
    yield call(toast.success, t('toastMessages.success.draftSentToEditor', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.submitBookDraftToEditorByDevelopmentalEditor[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* submitBookDraftToEditorByProProofreader(action) {
  try {
    yield call(
      API.Books.submitBookDraftToEditorByProProofreader,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.submitBookDraftToEditorByProProofreader[AsyncActionStatus.FULFILLED]());
    yield call(toast.success, t('toastMessages.success.draftSentToEditor', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.submitBookDraftToEditorByProProofreader[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* sendBookDraftToCustomerByEditor(action) {
  try {
    const response = yield call(
      API.Books.sendBookDraftToCustomerByEditor,
      action.payload.bookId,
    );
    Mixpanel.track('Editor draft approval');
    yield put(BooksActionCreators.sendBookDraftToCustomerByEditor[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield put(ModalsActionCreators.openDraftApprovedByEditorModal());
    yield call(toast.success, t('toastMessages.success.draftSentToCustomer', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.sendBookDraftToCustomerByEditor[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* approveBookDraftByCustomer(action) {
  try {
    const response = yield call(
      API.Books.approveBookDraftByCustomer,
      action.payload.bookId,
    );
    Mixpanel.track('Customer draft approval');
    yield put(BooksActionCreators.approveBookDraftByCustomer[AsyncActionStatus.FULFILLED](
      response.data.book,
      response.data.project,
    ));
    yield put(ModalsActionCreators.openDraftApprovedByCustomerModal());
    yield call(toast.success, t('toastMessages.success.draftApproved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.approveBookDraftByCustomer[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* sendBookDraftCustomerFeedback(action) {
  try {
    const response = yield call(
      API.Books.sendBookDraftCustomerFeedback,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.sendBookDraftCustomerFeedback[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.draftFeedbackSentByCustomer', { ns: i18next.Namespace.COMMON }));

    if (!response.data.isDraftRated) {
      yield put(ModalsActionCreators.openRateDraftModal());
    }
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.sendBookDraftCustomerFeedback[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* sendBookDraftEditorFeedback(action) {
  try {
    const response = yield call(
      API.Books.sendBookDraftEditorFeedback,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.sendBookDraftEditorFeedback[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.feedbackSentToWriter', { ns: i18next.Namespace.COMMON }));

    if (!response.data.isDraftRated) {
      yield put(ModalsActionCreators.openRateDraftModal());
    }
  } catch (error) {
    yield put(BooksActionCreators.sendBookDraftEditorFeedback[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* sendBookOutlineEditorFeedback(action) {
  try {
    const response = yield call(
      API.Books.sendBookOutlineEditorFeedback,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.sendBookOutlineEditorFeedback[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    if (!response.data.isOutlineRated && action.payload.isUserCanRateOutline) {
      yield put(ModalsActionCreators.openRateBookOutlineModal());
    }
    yield call(toast.success, t('toastMessages.success.feedbackSentToWriter', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.sendBookOutlineEditorFeedback[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* sendBookOutlineCustomerFeedback(action) {
  try {
    const response = yield call(
      API.Books.sendBookOutlineCustomerFeedback,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.sendBookOutlineCustomerFeedback[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.feedbackSentToWriter', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.sendBookOutlineCustomerFeedback[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* sendBookOutlineWriterFeedback(action) {
  try {
    const response = yield call(
      API.Books.sendBookOutlineWriterFeedback,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.sendBookOutlineWriterFeedback[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield call(action.payload.saveCallback, true);
  } catch (error) {
    yield put(BooksActionCreators.sendBookOutlineWriterFeedback[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* updateSpecificField(action) {
  try {
    const {
      bookId, field, value,
    } = action.payload;

    const response = yield call(
      API.Books.updateSpecificField,
      bookId,
      field,
      value,
    );
    yield put(BooksActionCreators.updateSpecificField[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    if (field === 'outlineApproveByCustomer.isOnboardingSeen' && value === true) {
      yield put(ModalsActionCreators.closeCustomerOutlineOnboardingModal());
    }
    if (field === 'draftSubmitByWriter.shouldWriterSeeEditorUpdates' && value === false) {
      yield put(ModalsActionCreators.closeEditorFeedbackToWriterModal());
    }
    if (field === 'draftApproveByEditor.isWriterUpdatesSeenByEditor' && value === true) {
      yield put(ModalsActionCreators.closeDraftIsUpdatedByWriterModal());
    }
  } catch (error) {
    yield put(BooksActionCreators.updateSpecificField[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* sendDraftToCustomerByWriter(action) {
  try {
    const response = yield call(
      API.Books.sendDraftToCustomerByWriter,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.sendDraftToCustomerByWriter[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.draftIsSentToCustomerByWriter', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.sendDraftToCustomerByWriter[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* sendPDFToMyEmail(action) {
  try {
    yield call(
      API.Books.sendPDFToMyEmail,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.sendPDFToMyEmail[AsyncActionStatus.FULFILLED]());
    yield call(toast.success, t('toastMessages.success.emailSent', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.sendPDFToMyEmail[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* sendPDFToPrinter(action) {
  try {
    const response = yield call(
      API.Books.sendPDFToPrinter,
      action.payload.bookId,
      action.payload.extraCopies,
    );
    Mixpanel.track(`${action.payload.printerType} sent to print`);
    yield put(BooksActionCreators.sendPDFToPrinter[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield put(ModalsActionCreators.closeSendToPrinterModal());
    yield put(ModalsActionCreators.openSuccessfullySentToPrinterModal());
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.sendPDFToPrinter[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* saveUserIdWhoReadNotificationDraftWasEdited(action) {
  try {
    const response = yield call(API.Books.saveUserIdWhoReadNotificationDraftWasEdited, action.payload.projectId);
    yield put(BooksActionCreators.saveUserIdWhoReadNotificationDraftWasEdited[AsyncActionStatus.FULFILLED](response.data));
    yield put(ModalsActionCreators.closeDraftEditedByCustomerModal());
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(BooksActionCreators.saveUserIdWhoReadNotificationDraftWasEdited[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, err.message);
  }
}

function* addCommentToBookSample(action) {
  try {
    const {
      bookId,
      commentId,
      comment,
      sampleDelta,
    } = action.payload;

    const response = yield call(
      API.Books.addCommentToBookSample,
      bookId,
      commentId,
      comment,
      sampleDelta,
    );
    yield put(BooksActionCreators.addCommentToBookSample[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.comment,
      response.data.bookSample,
    ));
  } catch (error) {
    yield put(BooksActionCreators.addCommentToBookSample[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* deleteCommentInBookSample(action) {
  try {
    const {
      bookId,
      commentId,
      sampleDelta,
    } = action.payload;

    const response = yield call(
      API.Books.deleteCommentInBookSample,
      bookId,
      commentId,
      sampleDelta,
    );
    yield put(BooksActionCreators.deleteCommentInBookSample[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.comment,
      response.data.bookSample,
    ));
  } catch (error) {
    yield put(BooksActionCreators.deleteCommentInBookSample[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* resolveCommentInBookSample(action) {
  try {
    const {
      bookId,
      commentId,
      sampleDelta,
    } = action.payload;

    const response = yield call(
      API.Books.resolveCommentInBookSample,
      bookId,
      commentId,
      sampleDelta,
    );
    yield put(BooksActionCreators.resolveCommentInBookSample[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.comment,
      response.data.bookSample,
    ));
  } catch (error) {
    yield put(BooksActionCreators.resolveCommentInBookSample[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getBookAndResourcesByProjectId(action) {
  try {
    const response = yield call(
      API.Books.getBookAndResourcesByProjectId,
      action.payload.projectId,
    );
    yield put(BooksActionCreators.getBookAndResourcesByProjectId[AsyncActionStatus.FULFILLED](
      response.data.book,
      response.data.users,
    ));
  } catch (error) {
    yield put(BooksActionCreators.getBookAndResourcesByProjectId[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* updateCommentInBookSample(action) {
  try {
    const {
      bookId,
      commentId,
      commentText,
    } = action.payload;

    const response = yield call(
      API.Books.updateCommentInBookSample,
      bookId,
      commentId,
      commentText,
    );
    yield put(BooksActionCreators.updateCommentInBookSample[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.comment,
    ));
  } catch (error) {
    yield put(BooksActionCreators.updateCommentInBookSample[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}


function* addReplyToCommentInBookSample(action) {
  try {
    const {
      bookId,
      commentId,
      reply,
    } = action.payload;
    const response = yield call(
      API.Books.addReplyToCommentInBookSample,
      bookId,
      commentId,
      reply._id,
      reply,
    );
    yield put(BooksActionCreators.addReplyToCommentInBookSample[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.commentId,
      response.data.reply,
    ));
  } catch (error) {
    yield put(BooksActionCreators.addReplyToCommentInBookSample[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}


function* updateReplyToCommentInBookSample(action) {
  try {
    const {
      bookId,
      commentId,
      replyId,
      replyText,
    } = action.payload;

    const response = yield call(
      API.Books.updateReplyToCommentInBookSample,
      bookId,
      commentId,
      replyId,
      replyText,
    );
    yield put(BooksActionCreators.updateReplyToCommentInBookSample[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.commentId,
      response.data.reply,
    ));
  } catch (error) {
    yield put(BooksActionCreators.updateReplyToCommentInBookSample[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getBookAndResourcesByCustomerId(action) {
  try {
    const response = yield call(
      API.Books.getBookAndResourcesByCustomerId,
      action.payload.userId,
    );
    yield put(BooksActionCreators.getBookAndResourcesByCustomerId[AsyncActionStatus.FULFILLED](
      response.data.book,
      response.data.users,
    ));
  } catch (error) {
    yield put(BooksActionCreators.getBookAndResourcesByCustomerId[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* watchDeleteOrUndoSampleComments() {
  const requestChannel = yield actionChannel(BooksActionTypes.deleteOrUndoComments[AsyncActionStatus.PENDING]);

  while (true) {
    const { payload } = yield take(requestChannel);
    yield call(deleteOrUndoComments, payload);
  }
}

function* deleteOrUndoComments(payload) {
  try {
    const books = yield select(BooksSelectors.selectAllBooks);
    const {
      bookId, deltaCommentsIds, fieldDelta, fieldName = 'bookSample',
    } = payload;
    const fieldComments = books.find(book => book._id === bookId)?.[fieldName]?.comments || [];

    const commentsToDelete = fieldComments.reduce((comments, c) => {
      if (!deltaCommentsIds.includes(c._id) && !c.requestedToDelete && !c.resolved) comments.push(c._id);
      return comments;
    }, []);

    const commentsToUndo = fieldComments.reduce((comments, c) => {
      if (deltaCommentsIds.includes(c._id) && c.requestedToDelete) comments.push(c._id);
      return comments;
    }, []);

    if (isEmpty(commentsToDelete) && isEmpty(commentsToUndo)) {
      throw new Error('There are no comments to be processed');
    }

    let updatedComments = [];
    if (!isEmpty(commentsToDelete)) {
      const response = yield call(API.Books.deleteComments, bookId, commentsToDelete, fieldDelta, fieldName);
      updatedComments = response.data.comments;
    } else if (!isEmpty(commentsToUndo)) {
      const response = yield call(API.Books.undoComments, bookId, commentsToUndo, fieldDelta, fieldName);
      updatedComments = response.data.comments;
    }

    yield put(BooksActionCreators.deleteOrUndoComments[AsyncActionStatus.FULFILLED](bookId, updatedComments, fieldName));
  } catch (error) {
    yield put(BooksActionCreators.deleteOrUndoComments[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* approveCoverByCustomer({ payload }) {
  try {
    const {
      bookId,
    } = payload;
    const response = yield call(API.Books.approveCoverByCustomer, bookId);
    yield put(BooksActionCreators.approveCoverByCustomer[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield put(ModalsActionCreators.openCoverNotificationModal());
  } catch (error) {
    yield put(BooksActionCreators.approveCoverByCustomer[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* completeInterviewStage(action) {
  try {
    const response = yield call(
      API.Books.completeInterviewStage,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.completeInterviewStage[AsyncActionStatus.FULFILLED](response.data.book));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.completeInterviewStage[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* approveOutlineByEditor(action) {
  try {
    const response = yield call(
      API.Books.approveOutlineByEditor,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.approveOutlineByEditor[AsyncActionStatus.FULFILLED](response.data.book));
    Mixpanel.track('Editor outline approval');
    yield put(ModalsActionCreators.openThankYouForApprovingOutlineModal());
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.approveOutlineByEditor[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* approveOutlineByCustomer(action) {
  try {
    const response = yield call(
      API.Books.approveOutlineByCustomer,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.approveOutlineByCustomer[AsyncActionStatus.FULFILLED](response.data.book));
    yield put(ModalsActionCreators.openThankYouForApprovingOutlineModal());
    Mixpanel.track('Customer outline approval');
    yield call(toast.success, t('toastMessages.success.outlineSubmittedSuccessfully', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.approveOutlineByCustomer[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* submitOutlineByWriter(action) {
  try {
    const response = yield call(
      API.Books.submitOutlineByWriter,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.submitOutlineByWriter[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(action.payload.saveCallback);
    Mixpanel.track('Writer outline submission');
    yield put(ModalsActionCreators.openOutlineSubmittedModal());
    yield call(toast.success, t('toastMessages.success.outlineSubmittedSuccessfully', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.submitOutlineByWriter[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* approveFinalProofByCustomer(action) {
  try {
    const response = yield call(
      API.Books.approveFinalProofByCustomer,
      action.payload.userId,
    );
    Mixpanel.track('Customer proof approval');
    yield put(BooksActionCreators.approveFinalProofByCustomer[AsyncActionStatus.FULFILLED](response.data.book));
    yield put(ModalsActionCreators.openProofApprovedByCustomerModal());
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.approveFinalProofByCustomer[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* approveFinalProofByEditor(action) {
  try {
    const response = yield call(
      API.Books.approveFinalProofByEditor,
      action.payload.projectId,
      action.payload.editorId,
    );
    Mixpanel.track('Editor proof approval');
    yield put(BooksActionCreators.approveFinalProofByEditor[AsyncActionStatus.FULFILLED](response.data.book));
    yield put(ModalsActionCreators.openProofApprovedByEditorModal());
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.approveFinalProofByEditor[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* editorSendDraftToDevelopmentalEditor(action) {
  try {
    const response = yield call(API.Books.editorSendDraftToDevelopmentalEditor, action.payload.bookId);

    yield put(BooksActionCreators.editorSendDraftToDevelopmentalEditor[AsyncActionStatus.FULFILLED](response.data.book, response.data.project));

    if (!response.data.isDraftRated) {
      yield put(ModalsActionCreators.openRateDraftModal());
    }
  } catch (err) {
    yield put(BooksActionCreators.editorSendDraftToDevelopmentalEditor[AsyncActionStatus.REJECTED](
      generateErrorMessage(err),
    ));
  }
}

function* editorSendDraftToProProofreader(action) {
  try {
    const response = yield call(API.Books.editorSendDraftToProProofreader, action.payload.bookId);

    yield put(BooksActionCreators.editorSendDraftToProProofreader[AsyncActionStatus.FULFILLED](response.data.book, response.data.project));

    if (!response.data.isDraftRated) {
      yield put(ModalsActionCreators.openRateDraftModal());
    }
  } catch (err) {
    yield put(BooksActionCreators.editorSendDraftToProProofreader[AsyncActionStatus.REJECTED](
      generateErrorMessage(err),
    ));
  }
}

function* uploadDocumentToGoogleDocx(action) {
  try {
    const response = yield call(
      API.Books.uploadDocumentToGoogleDocx,
      action.payload.bookId,
      action.payload.file,
    );

    yield put(BooksActionCreators.uploadDocumentToGoogleDocx[AsyncActionStatus.FULFILLED](response.data.book));
    window.open(response.data.link, '_blank');
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(BooksActionCreators.uploadDocumentToGoogleDocx[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* updateCoverCopy(action) {
  try {
    const response = yield call(API.Books.updateCoverCopy, action.payload.bookData);

    yield put(BooksActionCreators.updateCoverCopy[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.changesSaved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.updateCoverCopy[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* updateCoverOptions(action) {
  try {
    const response = yield call(API.Books.updateCoverOptions, action.payload.bookId, action.payload.bookData);

    yield put(BooksActionCreators.updateCoverOptions[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.changesSaved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.updateCoverOptions[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}


function* onCloseOnboardingFeedbackModal(action) {
  try {
    yield put(ModalsActionCreators.closeOnboardingFeedbackModal());
    const response = yield call(API.Books.closeOnboardingFeedbackModal, action.payload.projectId);
    yield put(BooksActionCreators.onCloseOnboardingFeedbackModal[AsyncActionStatus.FULFILLED](response.data));
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(BooksActionCreators.onCloseOnboardingFeedbackModal[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, err.message);
  }
}

function* addCommentToField(action) {
  try {
    const {
      bookId,
      commentId,
      comment,
      fieldDelta,
      fieldName,
    } = action.payload;

    const response = yield call(
      API.Books.addCommentToField,
      bookId,
      commentId,
      comment,
      fieldDelta,
      fieldName,
    );

    yield put(BooksActionCreators.addCommentToField[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.comment,
      response.data[fieldName],
      fieldName,
    ));
  } catch (error) {
    yield put(BooksActionCreators.addCommentToField[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}


function* deleteCommentByFieldName(action) {
  try {
    const {
      bookId,
      commentId,
      fieldDelta,
      fieldName,
    } = action.payload;

    const response = yield call(
      API.Books.deleteCommentByFieldName,
      bookId,
      commentId,
      fieldDelta,
      fieldName,
    );
    yield put(BooksActionCreators.deleteCommentByFieldName[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.comment,
      response.data[fieldName],
      fieldName,
    ));
  } catch (error) {
    yield put(BooksActionCreators.deleteCommentByFieldName[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* resolveCommentByFieldName(action) {
  try {
    const {
      bookId,
      commentId,
      fieldDelta,
      fieldName,
    } = action.payload;

    const response = yield call(
      API.Books.resolveCommentByFieldName,
      bookId,
      commentId,
      fieldDelta,
      fieldName,
    );
    yield put(BooksActionCreators.resolveCommentByFieldName[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.comment,
      response.data[fieldName],
      fieldName,
    ));
  } catch (error) {
    yield put(BooksActionCreators.resolveCommentByFieldName[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* updateCommentByFieldName(action) {
  try {
    const {
      bookId,
      commentId,
      commentText,
      fieldName,
    } = action.payload;

    const response = yield call(
      API.Books.updateCommentByFieldName,
      bookId,
      commentId,
      commentText,
      fieldName,
    );
    yield put(BooksActionCreators.updateCommentByFieldName[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.comment,
      fieldName,
    ));
  } catch (error) {
    yield put(BooksActionCreators.updateCommentByFieldName[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* addReplyToCommentByFieldName(action) {
  try {
    const {
      bookId,
      commentId,
      reply,
      fieldName,
    } = action.payload;
    const response = yield call(
      API.Books.addReplyToCommentByFieldName,
      bookId,
      commentId,
      reply._id,
      reply,
      fieldName,
    );
    yield put(BooksActionCreators.addReplyToCommentByFieldName[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.commentId,
      response.data.reply,
      fieldName,
    ));
  } catch (error) {
    yield put(BooksActionCreators.addReplyToCommentByFieldName[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* updateReplyToCommentByFieldName(action) {
  try {
    const {
      bookId,
      commentId,
      replyId,
      replyText,
      fieldName,
    } = action.payload;

    const response = yield call(
      API.Books.updateReplyToCommentByFieldName,
      bookId,
      commentId,
      replyId,
      replyText,
      fieldName,
    );
    yield put(BooksActionCreators.updateReplyToCommentByFieldName[AsyncActionStatus.FULFILLED](
      response.data.bookId,
      response.data.commentId,
      response.data.reply,
      fieldName,
    ));
  } catch (error) {
    yield put(BooksActionCreators.updateReplyToCommentByFieldName[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* saveBookSampleChanges(action) {
  try {
    const { bookId, bookSample } = action.payload;

    const response = yield call(API.Books.saveBookSampleChanges, bookId, bookSample);
    yield put(BooksActionCreators.saveBookSampleChanges[AsyncActionStatus.FULFILLED](
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.bookSampleUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.saveBookSampleChanges[AsyncActionStatus.REJECTED](
      generateErrorMessage(message),
    ));
    yield call(toast.error, message);
  }
}

function* approveBookSampleByCustomer(action) {
  try {
    const response = yield call(
      API.Books.approveBookSampleByCustomer,
      action.payload.bookId,
      action.payload.bookSample,
    );
    yield put(BooksActionCreators.approveBookSampleByCustomer[AsyncActionStatus.FULFILLED](response.data.book));
    yield put(ModalsActionCreators.openThankYouForApprovingSampleModal());
    Mixpanel.track('Customer sample approval');
    yield call(toast.success, t('toastMessages.success.sampleApproved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.approveBookSampleByCustomer[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* saveStyleSamplesChanges(action) {
  try {
    const response = yield call(
      API.Books.saveStyleSamplesChanges,
      action.payload.bookId,
      action.payload.styleSamples,
    );
    yield put(BooksActionCreators.saveStyleSamplesChanges[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.changesSaved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.saveStyleSamplesChanges[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* approveStyleSampleByCustomer(action) {
  try {
    const response = yield call(
      API.Books.approveStyleSampleByCustomer,
      action.payload.bookId,
      action.payload.selectedStyleSampleId,
    );
    yield put(BooksActionCreators.approveStyleSampleByCustomer[AsyncActionStatus.FULFILLED](response.data.book));
    yield put(ModalsActionCreators.openThankYouForStyleSampleApprovalModal());
    Mixpanel.track('Customer sample approval');
    yield call(toast.success, t('toastMessages.success.sampleApproved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.approveStyleSampleByCustomer[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* approveSampleByWriter(action) {
  try {
    const response = yield call(
      API.Books.approveSampleByWriter,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.approveSampleByWriter[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.sampleSubmitted', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.approveSampleByWriter[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* approveSampleByEditor(action) {
  try {
    const response = yield call(
      API.Books.approveSampleByEditor,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.approveSampleByEditor[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.sampleApproved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.approveSampleByEditor[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* submitSpecification(action) {
  try {
    const response = yield call(
      API.Books.submitSpecification,
      action.payload.bookId,
      action.payload.answers,
      action.payload.structure,
    );
    yield put(BooksActionCreators.submitSpecification[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.specificationUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.submitSpecification[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* saveSpecification(action) {
  try {
    const response = yield call(
      API.Books.saveSpecification,
      action.payload.bookId,
      action.payload.answers,
      action.payload.structure,
    );
    yield put(BooksActionCreators.saveSpecification[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.specificationUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.saveSpecification[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* uploadCustomPDF(action) {
  try {
    const response = yield call(
      API.Books.uploadCustomPDF,
      action.payload.bookId,
      action.payload.pdfUrl,
    );
    yield put(BooksActionCreators.uploadCustomPDF[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.pdfUploaded', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMessage = generateErrorMessage(error);
    yield call(toast.error, errMessage);
    yield put(BooksActionCreators.uploadCustomPDF[AsyncActionStatus.REJECTED](errMessage));
  }
}

function* uploadCustomCover(action) {
  try {
    const response = yield call(
      API.Books.uploadCustomCover,
      action.payload.bookId,
      action.payload.coverUrl,
    );
    yield put(BooksActionCreators.uploadCustomCover[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.coverUploaded', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMessage = generateErrorMessage(error);
    yield call(toast.error, errMessage);
    yield put(BooksActionCreators.uploadCustomCover[AsyncActionStatus.REJECTED](errMessage));
  }
}

function* addCommentToPDF(action) {
  try {
    const response = yield call(
      API.Books.addCommentToPDF,
      action.payload.bookId,
      action.payload.commentText,
      action.payload.pageNumber,
    );
    yield put(BooksActionCreators.addCommentToPDF[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.commentAdded', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.addCommentToPDF[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* resolvePdfComment(action) {
  try {
    const response = yield call(
      API.Books.resolvePdfComment,
      action.payload.bookId,
      action.payload.commentId,
    );
    yield put(BooksActionCreators.resolvePdfComment[AsyncActionStatus.FULFILLED](response.data.book));
  } catch (error) {
    yield put(BooksActionCreators.resolvePdfComment[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* deletePdfComment(action) {
  try {
    const response = yield call(
      API.Books.deletePdfComment,
      action.payload.bookId,
      action.payload.commentId,
    );
    yield put(BooksActionCreators.deletePdfComment[AsyncActionStatus.FULFILLED](response.data.book));
  } catch (error) {
    yield put(BooksActionCreators.deletePdfComment[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* addReplyToPdfComment(action) {
  try {
    const response = yield call(
      API.Books.addReplyToPdfComment,
      action.payload.bookId,
      action.payload.commentId,
      action.payload.replyText,
    );
    yield put(BooksActionCreators.addReplyToPdfComment[AsyncActionStatus.FULFILLED](response.data.book));
  } catch (error) {
    yield put(BooksActionCreators.addReplyToPdfComment[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* editPdfComment(action) {
  try {
    const response = yield call(
      API.Books.editPdfComment,
      action.payload.bookId,
      action.payload.commentId,
      action.payload.commentText,
    );
    yield put(BooksActionCreators.editPdfComment[AsyncActionStatus.FULFILLED](response.data.book));
  } catch (error) {
    yield put(BooksActionCreators.editPdfComment[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* approveDraftByContentTeam(action) {
  try {
    const response = yield call(API.Books.approveDraftByContentTeam, action.payload.bookId);
    yield put(BooksActionCreators.approveDraftByContentTeam[AsyncActionStatus.FULFILLED](
      response.data.book,
      response.data.project,
    ));
    yield call(toast.success, t('toastMessages.success.draftApproved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(BooksActionCreators.approveDraftByContentTeam[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* proofRequestReview(action) {
  try {
    yield call(
      API.Books.proofRequestReview,
      action.payload.bookId,
    );
    yield put(BooksActionCreators.proofRequestReview[AsyncActionStatus.FULFILLED]());
    yield call(toast.success, t('toastMessages.success.reviewRequestSuccessfully', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(BooksActionCreators.proofRequestReview[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

export default [
  watchDeleteOrUndoSampleComments(),
  takeEvery(
    BooksActionTypes.submitBookDraftToEditorByWriter[AsyncActionStatus.PENDING],
    submitBookDraftToEditorByWriter,
  ),
  takeEvery(
    BooksActionTypes.submitBookDraftToEditorByDevelopmentalEditor[AsyncActionStatus.PENDING],
    submitBookDraftToEditorByDevelopmentalEditor,
  ),
  takeEvery(
    BooksActionTypes.sendBookDraftToCustomerByEditor[AsyncActionStatus.PENDING],
    sendBookDraftToCustomerByEditor,
  ),
  takeEvery(
    BooksActionTypes.approveBookDraftByCustomer[AsyncActionStatus.PENDING],
    approveBookDraftByCustomer,
  ),
  takeEvery(
    BooksActionTypes.sendBookDraftCustomerFeedback[AsyncActionStatus.PENDING],
    sendBookDraftCustomerFeedback,
  ),
  takeEvery(
    BooksActionTypes.sendBookDraftEditorFeedback[AsyncActionStatus.PENDING],
    sendBookDraftEditorFeedback,
  ),
  takeEvery(
    BooksActionTypes.sendBookOutlineEditorFeedback[AsyncActionStatus.PENDING],
    sendBookOutlineEditorFeedback,
  ),
  takeEvery(
    BooksActionTypes.sendBookOutlineCustomerFeedback[AsyncActionStatus.PENDING],
    sendBookOutlineCustomerFeedback,
  ),
  takeEvery(
    BooksActionTypes.sendBookOutlineWriterFeedback[AsyncActionStatus.PENDING],
    sendBookOutlineWriterFeedback,
  ),
  takeEvery(
    BooksActionTypes.updateSpecificField[AsyncActionStatus.PENDING],
    updateSpecificField,
  ),
  takeEvery(
    BooksActionTypes.sendDraftToCustomerByWriter[AsyncActionStatus.PENDING],
    sendDraftToCustomerByWriter,
  ),
  takeEvery(
    BooksActionTypes.sendPDFToMyEmail[AsyncActionStatus.PENDING],
    sendPDFToMyEmail,
  ),
  takeEvery(
    BooksActionTypes.sendPDFToPrinter[AsyncActionStatus.PENDING],
    sendPDFToPrinter,
  ),
  takeEvery(
    BooksActionTypes.saveUserIdWhoReadNotificationDraftWasEdited[AsyncActionStatus.PENDING],
    saveUserIdWhoReadNotificationDraftWasEdited,
  ),
  takeEvery(
    BooksActionTypes.addCommentToBookSample[AsyncActionStatus.PENDING],
    addCommentToBookSample,
  ),
  takeEvery(
    BooksActionTypes.deleteCommentInBookSample[AsyncActionStatus.PENDING],
    deleteCommentInBookSample,
  ),
  takeEvery(
    BooksActionTypes.resolveCommentInBookSample[AsyncActionStatus.PENDING],
    resolveCommentInBookSample,
  ),
  takeEvery(
    BooksActionTypes.updateCommentInBookSample[AsyncActionStatus.PENDING],
    updateCommentInBookSample,
  ),
  takeEvery(
    BooksActionTypes.addReplyToCommentInBookSample[AsyncActionStatus.PENDING],
    addReplyToCommentInBookSample,
  ),
  takeEvery(
    BooksActionTypes.updateReplyToCommentInBookSample[AsyncActionStatus.PENDING],
    updateReplyToCommentInBookSample,
  ),
  takeEvery(
    BooksActionTypes.getBookAndResourcesByProjectId[AsyncActionStatus.PENDING],
    getBookAndResourcesByProjectId,
  ),
  takeEvery(
    BooksActionTypes.getBookAndResourcesByCustomerId[AsyncActionStatus.PENDING],
    getBookAndResourcesByCustomerId,
  ),
  takeEvery(
    BooksActionTypes.approveCoverByCustomer[AsyncActionStatus.PENDING],
    approveCoverByCustomer,
  ),
  takeEvery(
    BooksActionTypes.completeInterviewStage[AsyncActionStatus.PENDING],
    completeInterviewStage,
  ),
  takeEvery(
    BooksActionTypes.approveOutlineByEditor[AsyncActionStatus.PENDING],
    approveOutlineByEditor,
  ),
  takeEvery(
    BooksActionTypes.approveOutlineByCustomer[AsyncActionStatus.PENDING],
    approveOutlineByCustomer,
  ),
  takeEvery(
    BooksActionTypes.submitOutlineByWriter[AsyncActionStatus.PENDING],
    submitOutlineByWriter,
  ),
  takeEvery(
    BooksActionTypes.approveFinalProofByCustomer[AsyncActionStatus.PENDING],
    approveFinalProofByCustomer,
  ),
  takeEvery(
    BooksActionTypes.approveFinalProofByEditor[AsyncActionStatus.PENDING],
    approveFinalProofByEditor,
  ),
  takeEvery(
    BooksActionTypes.editorSendDraftToDevelopmentalEditor[AsyncActionStatus.PENDING],
    editorSendDraftToDevelopmentalEditor,
  ),
  takeEvery(
    BooksActionTypes.editorSendDraftToProProofreader[AsyncActionStatus.PENDING],
    editorSendDraftToProProofreader,
  ),
  takeEvery(
    BooksActionTypes.uploadDocumentToGoogleDocx[AsyncActionStatus.PENDING],
    uploadDocumentToGoogleDocx,
  ),
  takeEvery(
    BooksActionTypes.updateCoverCopy[AsyncActionStatus.PENDING],
    updateCoverCopy,
  ),
  takeEvery(
    BooksActionTypes.updateCoverOptions[AsyncActionStatus.PENDING],
    updateCoverOptions,
  ),
  takeEvery(
    BooksActionTypes.onCloseOnboardingFeedbackModal[AsyncActionStatus.PENDING],
    onCloseOnboardingFeedbackModal,
  ),
  takeEvery(
    BooksActionTypes.addCommentToField[AsyncActionStatus.PENDING],
    addCommentToField,
  ),
  takeEvery(
    BooksActionTypes.deleteCommentByFieldName[AsyncActionStatus.PENDING],
    deleteCommentByFieldName,
  ),
  takeEvery(
    BooksActionTypes.resolveCommentByFieldName[AsyncActionStatus.PENDING],
    resolveCommentByFieldName,
  ),
  takeEvery(
    BooksActionTypes.updateCommentByFieldName[AsyncActionStatus.PENDING],
    updateCommentByFieldName,
  ),
  takeEvery(
    BooksActionTypes.addReplyToCommentByFieldName[AsyncActionStatus.PENDING],
    addReplyToCommentByFieldName,
  ),
  takeEvery(
    BooksActionTypes.updateReplyToCommentByFieldName[AsyncActionStatus.PENDING],
    updateReplyToCommentByFieldName,
  ),
  takeEvery(
    BooksActionTypes.saveBookSampleChanges[AsyncActionStatus.PENDING],
    saveBookSampleChanges,
  ),
  takeEvery(
    BooksActionTypes.approveBookSampleByCustomer[AsyncActionStatus.PENDING],
    approveBookSampleByCustomer,
  ),
  takeEvery(
    BooksActionTypes.saveStyleSamplesChanges[AsyncActionStatus.PENDING],
    saveStyleSamplesChanges,
  ),
  takeEvery(
    BooksActionTypes.approveStyleSampleByCustomer[AsyncActionStatus.PENDING],
    approveStyleSampleByCustomer,
  ),
  takeEvery(
    BooksActionTypes.approveSampleByWriter[AsyncActionStatus.PENDING],
    approveSampleByWriter,
  ),
  takeEvery(
    BooksActionTypes.approveSampleByEditor[AsyncActionStatus.PENDING],
    approveSampleByEditor,
  ),
  takeEvery(
    BooksActionTypes.submitSpecification[AsyncActionStatus.PENDING],
    submitSpecification,
  ),
  takeEvery(
    BooksActionTypes.saveSpecification[AsyncActionStatus.PENDING],
    saveSpecification,
  ),
  takeEvery(
    BooksActionTypes.uploadCustomPDF[AsyncActionStatus.PENDING],
    uploadCustomPDF,
  ),
  takeEvery(
    BooksActionTypes.uploadCustomCover[AsyncActionStatus.PENDING],
    uploadCustomCover,
  ),
  takeEvery(
    BooksActionTypes.addCommentToPDF[AsyncActionStatus.PENDING],
    addCommentToPDF,
  ),
  takeEvery(
    BooksActionTypes.resolvePdfComment[AsyncActionStatus.PENDING],
    resolvePdfComment,
  ),
  takeEvery(
    BooksActionTypes.deletePdfComment[AsyncActionStatus.PENDING],
    deletePdfComment,
  ),
  takeEvery(
    BooksActionTypes.addReplyToPdfComment[AsyncActionStatus.PENDING],
    addReplyToPdfComment,
  ),
  takeEvery(
    BooksActionTypes.editPdfComment[AsyncActionStatus.PENDING],
    editPdfComment,
  ),
  takeEvery(
    BooksActionTypes.approveDraftByContentTeam[AsyncActionStatus.PENDING],
    approveDraftByContentTeam,
  ),
  takeEvery(
    BooksActionTypes.submitBookDraftToEditorByProProofreader[AsyncActionStatus.PENDING],
    submitBookDraftToEditorByProProofreader,
  ),
  takeEvery(
    BooksActionTypes.proofRequestReview[AsyncActionStatus.PENDING],
    proofRequestReview,
  ),
];
